import { mount } from 'mithril'
import { nodes } from '../../../utils/dom'
import { adStore } from '../../stores/adStore'
import { queryString } from '../../../utils/window'
import { noop, makeSafeFn } from '../../../utils/fn'
import { info, warn } from '../../../application/console'
import RegularAdView from '../../views/ads/RegularAdView'
import InjectedAdView from '../../views/ads/InjectedAdView'
import { AD_TYPES, AD_FORMATS } from '../../config/adsConfig'
import providerFactory from '../../../providers/ads/providerFactory'
import { locale } from '../../../providers/i18nProvider/i18nProvider'
import { SELECTORS } from '../../../config/preRenderedSelectorsConfig'
import { getPlatform, getIsMobile, viewport, getIsNativeApp } from '../../../utils/ua'
import {
  get as getFromPdo,
  ads as adsGlobalConfig,
  getIsEmbededPost,
  getIsDraft,
  getIsSlideshow,
} from '../../../application/pageDataObject'
import initAmazonHeaderBidder, { isCountryCodeUnitedStates } from './initAmazonHeaderBidder'

const SLIDESHOW_DISABLED_AD_FORMATS = [AD_FORMATS.Outofpage]
const adsConfig = getFromPdo('config.ads', require('../../config/adsConfig'))

const EXCLUDED_GEOS_FOR_MOBILE_BOTTOM_SLOT = ['in', 'ar', 'th', 'mx']
const { noAds, noads, debugAds } = queryString

function getAdType(adId) {
  return adsConfig.slot[adId].type
}

function getAdView(adType, ...args) {
  let AdViewToCreate
  switch (adType) {
    case AD_TYPES.REGULAR:
      AdViewToCreate = RegularAdView
      break
    case AD_TYPES.INJECTED:
      AdViewToCreate = InjectedAdView
      break
    default:
      AdViewToCreate = RegularAdView
  }
  return AdViewToCreate.create(...args)
}

function isAdsEnabled() {
  const { shouldShowAds } = adsGlobalConfig
  const page = getFromPdo('page', null)
  const areAdsEnabled = !(noAds || noads) && page && !getIsDraft() && shouldShowAds
  debugAds &&
    info('areAdsEnabled:', areAdsEnabled, {
      shouldShowAds,
      isEmbededPost: getIsEmbededPost(),
      isDraft: getIsDraft(),
      page,
    })
  return areAdsEnabled
}

function filterAdsByTypeDeviceAndGeo(adId, countryCode) {
  return (
    EXCLUDED_GEOS_FOR_MOBILE_BOTTOM_SLOT.includes(countryCode.toLowerCase()) &&
    adId === AD_FORMATS.Bottom &&
    getIsMobile()
  )
}

function isSlotEnabledInCountryCode(whiteListedCountryCodes, countryCode) {
  return whiteListedCountryCodes.map(s => s.toLowerCase()).includes(countryCode.toLowerCase())
}

function isDisabledInSlideshow(adId) {
  return getIsSlideshow() && SLIDESHOW_DISABLED_AD_FORMATS.includes(adId)
}

function notShow(adId, countryCode) {
  // eslint-disable-line complexity
  const slotConf = adsConfig.slot[adId]
  if (adId === 'Top' && getIsMobile()) {
    return true
  }
  if (!slotConf) {
    return true
  }
  let { type: adType, doubleClick: slotConfig, isDisabled, enabledInCountryCodes } = adsConfig.slot[
    adId
  ]
  if (isDisabled || adType === AD_TYPES.VIDEO || filterAdsByTypeDeviceAndGeo(adId, countryCode)) {
    return true
  }
  if (isDisabledInSlideshow(adId)) {
    return true
  }
  if (enabledInCountryCodes) {
    if (!isSlotEnabledInCountryCode(enabledInCountryCodes, countryCode)) {
      return true
    }
  }
  const {
    hideForPlatform,
    hideForNonMobile,
    hideForLocale,
    hideForHorizonalBreakpoint,
    isEnabledInNativeApp,
  } = slotConfig
  const isInDisallowedPlatform = hideForPlatform && getPlatform(hideForPlatform)
  const isHideForNonMobile = hideForNonMobile && !getIsMobile()
  const isInDisallowedLocale = hideForLocale === locale
  const isHiddenInNativeApp = !isEnabledInNativeApp && getIsNativeApp()
  const isInDisallowedViewport = hideForHorizonalBreakpoint && !viewport.widerThanTabletBreakpoint
  return (
    isInDisallowedPlatform ||
    isInDisallowedLocale ||
    isInDisallowedViewport ||
    isHideForNonMobile ||
    isHiddenInNativeApp ||
    false
  )
}

const pageAdSlots = {}

function mountAdComponent(adElement, countryCode) {
  const { dataset } = adElement
  const { slotId } = dataset
  const existingPageSlot = pageAdSlots[slotId]
  if (existingPageSlot) {
    warn(`slotId '${slotId}' is already mounted! Not mounting again.`, {
      newPageSlot: adElement,
      existingPageSlot,
      adsConfig,
      newDataset: dataset,
      existingDataset: existingPageSlot.dataset,
    })
    return false
  }
  pageAdSlots[slotId] = adElement
  if (makeSafeFn(notShow).invoke(slotId, countryCode)) {
    return false
  }
  const adType = makeSafeFn(getAdType).invoke(slotId)
  if (!adType) {
    warn(`'${adType}' is falsy! wat o_O`, {
      adElement,
      adsConfig,
      slotId,
    })
    return false
  }
  const adConfig = adsConfig.slot[slotId]
  const adView = getAdView(adType, adElement, adConfig, adStore)
  if (!adView) {
    warn('adView is falsy! How does even?', {
      adElement,
      adsConfig,
      adConfig,
      adStore,
      adType,
      adView,
    })
    return false
  }
  const adComponent = {
    view: adView.view.bind(adView),
    controller: noop,
  }
  return mount(adElement, adComponent)
}

function renderSlots(countryCode) {
  const adElements = nodes(SELECTORS.AD)
  for (const adElement of adElements) {
    mountAdComponent(adElement, countryCode)
  }
  if (isCountryCodeUnitedStates(countryCode)) {
    initAmazonHeaderBidder()
  }
}

export default function initAds() {
  const isEnabled = isAdsEnabled()
  adStore.isAdsEnabled = isEnabled
  if (isEnabled) {
    const Provider = providerFactory.getProvider()
    Provider.create(renderSlots)
  }
  return isEnabled
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/initializers/ads/initAds.js